import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { createModule } from '@entities/Module';
import { useProfileQuery } from '@entities/Profile';
import { FullScreenLoader, useAuthStore } from '@entities/User';
import { armDashboardTour } from '@features/Dashboard';
import {
  clearTrainingResult,
  FIRST_TRAINING_EXERCISES,
  FIRST_TRAINING_SIZE,
  isCardsLandingPath,
  OnboardingIntro,
  onboardingErrorKind,
  onboardingStepSearch,
  resumableTrainingModule,
  startOnboarding,
  trainingLeftInThisTab,
  useFinishOnboardingMutation,
  useOnboardingStepViewed,
  type StartedModule,
  type StarterChoice,
} from '@features/Onboarding';
import { fetchActiveCramSession, fetchActiveSession, startCramSession, StartSessionError } from '@features/Session';
import { useDemoInfoQuery } from '@features/Stories';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { visitLandingPath } from '@shared/lib/attribution';
import { peekReturnTo, rememberReturnTo } from '@shared/lib/returnTo';

/**
 * Экран 1 знакомства. Вернулся посреди пяти слов (закрыл вкладку, перезагрузил) — сразу в ту же
 * тренировку, на то же слово (B5, 2026-10-20): нового экрана и «Продолжить» не нужно. После «Назад»
 * из тренировки в этой вкладке обратно не перекидываем — экран показывается, «Начать» её продолжит.
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [checking, setChecking] = useState(() => !trainingLeftInThisTab());

  useEffect(() => {
    if (!checking) return;
    let cancelled = false;
    fetchActiveSession()
      .then((session) => {
        if (cancelled) return;
        const moduleId = resumableTrainingModule(session);
        if (moduleId) {
          navigate(
            { pathname: paths.onboardingTraining, search: onboardingStepSearch(search, moduleId) },
            { replace: true },
          );
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        // Не узнали — показываем экран: «Начать» само проверит тренировку выбранного модуля.
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [checking, navigate, search]);

  if (checking) return <FullScreenLoader />;
  return <OnboardingIntroScreen />;
}

/**
 * «Начать» — копия выбранного модуля (`/onboarding/start`), настоящая тренировка на 5 карточек (ввод и
 * выбор перевода, без финального матча; первая карточка — выбор) и переход в неё; незаконченная тренировка этого
 * модуля продолжается, а не начинается заново. «Пропустить» — тоже стартовый модуль, затем finish-onboarding
 * и главная. «Играть» (2026-10-30, экран «С чего начнём?») — то же, что «Пропустить», но дальше — миссия дня.
 * «Создать карточку» (2026-11-02) — то же, плюс свой модуль «Мои слова», дальше — строка создания с ним.
 */
function OnboardingIntroScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const finish = useFinishOnboardingMutation();
  const [busy, setBusy] = useState<'start' | 'skip' | 'mission' | 'card' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);
  const skipTracked = useRef(false);
  // Шёл в игру (регистрация из демо или ролика, `?next=/stories…`): тренировку слов не навязываем — тот же путь,
  // что «Пропустить», и сразу в миссию (решение владельца 2026-10-28: ролик обещал игру, а не карточки).
  const [gameFirst] = useState(() => !!peekReturnTo()?.startsWith(paths.stories));
  // «С чего начнём?» (решение владельца 2026-10-30): миссия дня или 5 слов. Без подтверждённой почты миссия идёт в
  // лимитах демо — кончились на сегодня, остаётся прежний экран. Первой — миссия у пришедших из демо игры (ролики),
  // у остальных (поиск, ChatGPT) — слова.
  const verified = useAuthStore((s) => !!s.user?.emailVerified);
  const demoInfo = useDemoInfoQuery();
  const missionOpen = verified || demoInfo.data?.available !== false;
  const [missionFirst] = useState(() => !!peekReturnTo()?.startsWith('/demo'));
  // «Свои слова» (решение владельца 2026-11-02): с подтверждённой почтой или пока бесплатная карточка не потрачена.
  // Первой — у пришедших с лендинговой страницы про карточки (cookie визита: атрибуцию регистрация уже стёрла).
  const profile = useProfileQuery();
  const cardOpen = verified || profile.data?.guest_card_available === true;
  const [cardFirst] = useState(() => isCardsLandingPath(visitLandingPath()));
  // Свой модуль «Мои слова», уже созданный с этого экрана: повтор после сбоя не создаёт второй.
  const myWords = useRef<string | null>(null);
  // Модуль, уже добавленный с этого экрана: повтор после сбоя старта тренировки не шлёт его снова.
  const added = useRef<{ sourceId: string | undefined; module: StartedModule } | null>(null);
  useOnboardingStepViewed('intro');

  const addStarter = async (
    sourceId: string | undefined,
    alternative: boolean,
    via: 'start' | 'skip' | 'mission' | 'card',
  ) => {
    if (added.current && (via === 'skip' || added.current.sourceId === sourceId)) return added.current.module;
    const module = await startOnboarding(sourceId);
    added.current = { sourceId, module };
    trackEvent('starter_module_added', { source: module.sourceId ?? sourceId ?? null, alternative, via });
    return module;
  };

  const lock = (kind: 'start' | 'skip' | 'mission' | 'card') => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(kind);
    setError(null);
    return true;
  };

  const unlock = (message: string) => {
    busyRef.current = false;
    setBusy(null);
    setError(message);
  };

  const handleStart = async ({ sourceId, alternative }: StarterChoice) => {
    if (!lock('start')) return;
    if (missionOpen) trackEvent('onboarding_choice', { option: 'words', first: !missionFirst });
    try {
      const module = await addStarter(sourceId, alternative, 'start');
      const toTraining = () =>
        navigate({ pathname: paths.onboardingTraining, search: onboardingStepSearch(search, module.moduleId) });
      // Тренировка этого модуля уже идёт (ушёл «Назад» посреди слов) — продолжаем её: новый старт
      // отменил бы прежнюю вместе с ответами. Не узнали — стартуем, как раньше.
      const active = await fetchActiveCramSession(module.moduleId).catch(() => null);
      if (resumableTrainingModule(active)) {
        toTraining();
        return;
      }
      const session = await startCramSession(module.moduleId, 'builder', {
        exercises: [...FIRST_TRAINING_EXERCISES],
        withBoss: false,
        limit: FIRST_TRAINING_SIZE,
      });
      trackEvent('first_training_started', { total: session.total });
      toTraining();
    } catch (err) {
      const network =
        (err instanceof StartSessionError && err.code === 'NETWORK') || onboardingErrorKind(err) === 'network';
      unlock(
        network
          ? t('onboarding.intro.networkError')
          : onboardingErrorKind(err) === 'rate_limit'
            ? t('onboarding.intro.rateLimit')
            : t('onboarding.intro.startError'),
      );
    }
  };

  const handleSkip = async () => {
    if (!lock('skip')) return;
    if (!skipTracked.current) {
      skipTracked.current = true;
      trackEvent('onboarding_skipped', { step: 'intro', ...(gameFirst ? { reason: 'game' } : {}) });
    }
    armDashboardTour();
    try {
      await addStarter(undefined, false, 'skip');
    } catch {
      // Модуль не добавился — знакомство всё равно завершаем: «Пропустить» не должен запирать ученика.
    }
    try {
      // Успех сменит стадию на app: OnboardingGate уведёт на главную (или в returnTo).
      await finish.mutateAsync({ module_ids: [] });
      clearTrainingResult();
    } catch (err) {
      unlock(onboardingErrorKind(err) === 'network' ? t('onboarding.intro.networkError') : t('onboarding.errorRetry'));
    }
  };

  // Миссия дня: стартовый модуль (как у «Пропустить» — слова ждут на главной), знакомство завершено, дальше
  // OnboardingGate ведёт в returnTo — `/stories?play=daily` сам запускает миссию.
  const handleMission = async () => {
    if (!lock('mission')) return;
    trackEvent('onboarding_choice', { option: 'mission', first: missionFirst });
    rememberReturnTo(`${paths.stories}?play=daily`);
    armDashboardTour();
    try {
      await addStarter(undefined, false, 'mission');
    } catch {
      // Модуль не добавился — в миссию всё равно пускаем: слова можно взять потом с главной.
    }
    try {
      await finish.mutateAsync({ module_ids: [] });
      clearTrainingResult();
    } catch (err) {
      unlock(onboardingErrorKind(err) === 'network' ? t('onboarding.intro.networkError') : t('onboarding.errorRetry'));
    }
  };

  // «Создать карточку»: стартовый модуль (как у миссии — на главной будет что тренировать), свой модуль «Мои слова»,
  // знакомство завершено, дальше OnboardingGate ведёт в returnTo — «Слова и модули» со строкой создания в этом модуле.
  const handleCard = async () => {
    if (!lock('card')) return;
    trackEvent('onboarding_choice', { option: 'card', first: cardFirst });
    armDashboardTour();
    try {
      await addStarter(undefined, false, 'card');
    } catch {
      // Модуль не добавился — карточку всё равно даём сделать.
    }
    if (!myWords.current) {
      try {
        const created = await createModule({
          name: t('onboarding.intro.cardModule'),
          cefrLevel: 'B1',
          sourceLang: 'en',
          targetLang: 'ru',
        });
        myWords.current = created.id;
      } catch {
        // Не создался (имя занято, сеть) — строка выберет модуль сама.
      }
    }
    rememberReturnTo(`${paths.words}?compose=${myWords.current ?? ''}`);
    try {
      await finish.mutateAsync({ module_ids: [] });
      clearTrainingResult();
    } catch (err) {
      unlock(onboardingErrorKind(err) === 'network' ? t('onboarding.intro.networkError') : t('onboarding.errorRetry'));
    }
  };

  const autoSkipped = useRef(false);
  useEffect(() => {
    if (!gameFirst || autoSkipped.current) return;
    autoSkipped.current = true;
    void handleSkip();
    // handleSkip новый на каждом рендере; пропуск — один раз при открытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameFirst]);

  // Пока пропускаем — загрузка; не вышло — обычный экран с ошибкой и обеими кнопками.
  if (gameFirst && !error) return <FullScreenLoader />;
  return (
    <OnboardingIntro
      busy={busy}
      error={error}
      onStart={handleStart}
      onSkip={handleSkip}
      mission={missionOpen ? { first: missionFirst } : null}
      onMission={handleMission}
      card={cardOpen ? { first: cardFirst } : null}
      onCard={handleCard}
    />
  );
}
